import { toast } from "react-toastify";
import { apiClient } from "../../../api/httpClient";
import { createSupportRequest } from "../../../services/supportRequestService";
import logger from "../../../globals/utils/logger";
import { formatFieldKeyLabels } from "./bondWizardStepHelpers";
import { ROLE_HOME_MAP } from "./bondFormConfig";

/**
 * @param {string} cache_id
 * @param {string} logContext
 * @param {React.Dispatch<React.SetStateAction<object>>} setGlobalState
 * @param {React.Dispatch<React.SetStateAction<number>>} setActiveStep
 */
export async function loadBondCacheFromParams(
  cache_id,
  logContext,
  setGlobalState,
  setActiveStep
) {
  try {
    const data = await apiClient(`/bond-cache/${cache_id}/`);
    let body = data?.request_body;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch (err) {
        logger.debug(logContext, "bond-cache nested body parse failed", { err });
      }
    }
    if (body?.state) setGlobalState((prev) => ({ ...prev, ...body.state }));
    if (typeof body?.activeStep === "number") setActiveStep(body.activeStep);
  } catch (err) {
    logger.warn(logContext, "bond-cache load failed", { err });
    toast.error("Failed to load saved progress");
  }
}

/**
 * @param {import("./bondFormConfig").BondFormKindConfig} config
 * @param {object} params
 */
export async function saveBondFormProgress(config, params) {
  const {
    userdetails,
    cacheId,
    cache_id,
    activeStep,
    globalState,
    navigate,
    setCacheId,
    setIsSaving,
  } = params;

  try {
    setIsSaving(true);
    if (!userdetails?.id) {
      toast.error("Unable to save: Login to save your progress");
      return;
    }

    const name = config.getSaveName(globalState);
    const saved = await apiClient("/bond-cache/", "POST", {
      cache_id: cacheId || cache_id,
      requester_id: userdetails.id,
      bond_type: config.bondTypeLabel,
      name: name || config.untitledSaveName,
      request_body: JSON.stringify({ activeStep, state: globalState }),
    });
    const returnedId = saved?.bond_cache_id || saved?.cache_id || saved?.id;
    if (returnedId) {
      if (returnedId !== cacheId) setCacheId(returnedId);
      if (!cache_id) {
        navigate(`${config.cacheRoutePrefix}${returnedId}`, { replace: true });
      }
    }
    toast.success("Progress saved");
  } catch (error) {
    toast.error(
      (error && (error.detail || error.error)) || "Failed to save progress"
    );
  } finally {
    setIsSaving(false);
  }
}

/**
 * @param {import("./bondFormConfig").BondFormKindConfig} config
 * @param {object} params
 */
export async function submitBondSupportRequest(config, params) {
  const {
    activeStep,
    globalState,
    failedBondType,
    description,
    navigate,
    role,
    logContext,
    setSupportModalOpen,
  } = params;

  try {
    await createSupportRequest({
      request_body: JSON.stringify({ activeStep, state: globalState }),
      bond_type: failedBondType,
      name: config.getSupportName(globalState),
      user_comment: description,
    });
    toast.success("Support request created successfully.");
    setSupportModalOpen(false);
    navigate(ROLE_HOME_MAP[role] || "/");
    return true;
  } catch (err) {
    logger.debug(logContext, "support request failed", { err });
    toast.error("Failed to submit support request.");
    return false;
  }
}

/**
 * @param {object} params
 * @returns {boolean} true when navigation should proceed
 */
export function validateStepClickNavigation(params) {
  const { stepIndex, activeStep, globalState, validateStepData, setErrors, setActiveStep } =
    params;

  if (stepIndex < activeStep) {
    setActiveStep(stepIndex);
    return false;
  }

  const currentStepData = globalState[Object.keys(globalState)[activeStep]];
  const currentStepErrors = validateStepData(currentStepData, activeStep, globalState);
  if (Object.keys(currentStepErrors).length > 0) {
    setErrors(currentStepErrors);
    const errorFields = formatFieldKeyLabels(Object.keys(currentStepErrors));
    toast.error(`Please complete the following fields: ${errorFields}`);
    return false;
  }

  setActiveStep(stepIndex);
  setErrors({});
  return true;
}

/**
 * @param {string} stepKey
 * @param {object} stepData
 */
export function mergeBondStepState(stepKey, stepData) {
  if (stepKey === "guardianDetails") {
    return {
      ...stepData,
      guardians: Array.isArray(stepData.guardians) ? stepData.guardians : [],
    };
  }
  return stepData;
}
